// Game version — single source of truth (update here + package.json + CHANGELOG.md)
export const GAME_VERSION = '0.4.8';

// Fixed internal resolution — all UI positions are relative to these
export const CANVAS_WIDTH = 1200;
export const CANVAS_HEIGHT = 800;


// Grid & map constants (mutable — updated when a map is selected)
export const TILE_SIZE = 40;
export let MAP_COLS = 20;
export let MAP_ROWS = 15;
export let GAME_WIDTH = MAP_COLS * TILE_SIZE;   // 800
export let GAME_HEIGHT = MAP_ROWS * TILE_SIZE;  // 600

// Called when a new map is selected to update dimensions
export function setMapDimensions(cols: number, rows: number): void {
  MAP_COLS = cols;
  MAP_ROWS = rows;
  GAME_WIDTH = cols * TILE_SIZE;
  GAME_HEIGHT = rows * TILE_SIZE;
}

// Tile types
export enum TileType {
  GROUND = 0,
  WALL = 1,
  PATH = 2,
  PLACEMENT = 3,   // Robot placement (metal texture)
  BASE = 4,
  SPAWN = 5,
  MUD = 6,         // Slows ground units
  WATER = 7,       // Blocks ground passage
  GRILLE = 8,      // Special unit placement (future)
  MAGMA = 9,       // Damages ground units passing through
  BROKEN_PLACEMENT = 10, // Damaged placement zone (reduced bonus)
}

// Actor types (runtime entities on the map: bases, future turrets/NPCs)
export enum ActorType {
  PLAYER_BASE = 'player_base',
  ENEMY_BASE = 'enemy_base',
}

// Decoration types (placed on top of map tiles, purely visual + minor effects)
export enum DecorType {
  TREE = 'tree',
  RUINS = 'ruins',
  COLUMN = 'column',
  CHASM = 'chasm',       // gouffre
  CONDUIT = 'conduit',
  BARREL = 'barrel',
  BROKEN_WALL = 'broken_wall',
  CAISSE = 'caisse',     // crate
  DEAD_TREE = 'dead_tree',
  FLAQUE = 'flaque',     // puddle
  ROCHER = 'rocher',     // rock
  SOUCHE = 'souche',     // stump
  CONNARDIUM = 'connardium',      // Connardium Mineral
  GARBAGE = 'garbage',             // trash pile
  BARRIERE = 'barriere',           // wooden fence
  BROKEN_STATUE = 'broken_statue', // broken statue
}

// Decoration definition
export interface DecorDef {
  type: DecorType;
  col: number;
  row: number;
}

// ── Decoration interaction types ──
export enum DecorInteraction {
  BLOCK = 'block',             // impassable obstacle
  KILL = 'kill',               // unit dies when fully on it
  SLOW = 'slow',               // slows unit movement
  DESTRUCTIBLE = 'destructible', // blocks movement, can be harvested/destroyed
  NONE = 'none',               // no interaction (purely visual)
}

/** Per-decoration interaction config */
export interface DecorConfig {
  interaction: DecorInteraction;
  slowMultiplier?: number; // only for SLOW type (0.5 = half speed)
}

export const DECOR_INTERACTIONS: Record<DecorType, DecorConfig> = {
  // Kill zones
  [DecorType.CHASM]:          { interaction: DecorInteraction.KILL },
  // Slow zones
  [DecorType.FLAQUE]:         { interaction: DecorInteraction.SLOW, slowMultiplier: 0.5 },
  // Harvestable resources (block movement until harvested by excavator)
  [DecorType.BARREL]:         { interaction: DecorInteraction.BLOCK },
  [DecorType.CAISSE]:         { interaction: DecorInteraction.BLOCK },
  [DecorType.CONNARDIUM]:     { interaction: DecorInteraction.BLOCK },
  [DecorType.GARBAGE]:        { interaction: DecorInteraction.BLOCK },
  // Impassable obstacles
  [DecorType.TREE]:           { interaction: DecorInteraction.BLOCK },
  [DecorType.RUINS]:          { interaction: DecorInteraction.BLOCK },
  [DecorType.COLUMN]:         { interaction: DecorInteraction.BLOCK },
  [DecorType.CONDUIT]:        { interaction: DecorInteraction.BLOCK },
  [DecorType.BROKEN_WALL]:    { interaction: DecorInteraction.BLOCK },
  [DecorType.DEAD_TREE]:      { interaction: DecorInteraction.BLOCK },
  [DecorType.ROCHER]:         { interaction: DecorInteraction.BLOCK },
  [DecorType.SOUCHE]:         { interaction: DecorInteraction.BLOCK },
  [DecorType.BARRIERE]:       { interaction: DecorInteraction.BLOCK },
  [DecorType.BROKEN_STATUE]:  { interaction: DecorInteraction.BLOCK },
};

// Resource yields for harvestable decorations (co = Connardium, electro = Electronic scrap)
export interface DecorResourceYield {
  co: number;
  electro: number;
  hp: number;       // resource HP — excavator must mine it down to 0 before collecting
}

/** Weapon/explosion damage reduction on resources (95% = nearly immune to gunfire) */
export const RESOURCE_WEAPON_RESISTANCE = 0.95;

export const DECOR_RESOURCE_YIELDS: Partial<Record<DecorType, DecorResourceYield>> = {
  [DecorType.CONNARDIUM]: { co: 5, electro: 0, hp: 160 },
  [DecorType.GARBAGE]:    { co: 0, electro: 5, hp: 80  },
  [DecorType.RUINS]:      { co: 3, electro: 3, hp: 100 },
  [DecorType.CAISSE]:     { co: 0, electro: 1, hp: 60  },
  [DecorType.BARREL]:     { co: 1, electro: 0, hp: 40  },
};

// Colors for placeholder rendering
export const TILE_COLORS: Record<TileType, number> = {
  [TileType.GROUND]:    0x2a2a3e,
  [TileType.WALL]:      0x555577,
  [TileType.PATH]:      0x3a3a2a,
  [TileType.PLACEMENT]: 0x2a3a2a,
  [TileType.BASE]:      0x2244aa,
  [TileType.SPAWN]:     0xaa2222,
  [TileType.MUD]:       0x665533,
  [TileType.WATER]:     0x224488,
  [TileType.GRILLE]:    0x444455,
  [TileType.MAGMA]:     0xcc4411,
  [TileType.BROKEN_PLACEMENT]: 0x3a2a2a,
};

// Enemy type identifier
export enum EnemyType {
  GRUNT = 'grunt',
  RUNNER = 'runner',
  TANK = 'tank',
  SWARM = 'swarm',
  BRUTE = 'brute',
  PHANTOM = 'phantom',
  DRONE = 'drone',
  BOMBER = 'bomber',
  WASP = 'wasp',
  SPECTER = 'specter',
  HIVE = 'hive',
  HEALER = 'healer',
}

// ── Enemy grade system ──
// Each grade modifies HP, speed, and damage with specific multipliers.
// HP and speed are inversely correlated: tanky grades are slow, fast grades are fragile.
export enum EnemyGrade {
  NORMAL = 'normal',
  ELITE = 'elite',     // red tint
  BOSS = 'boss',       // blue tint
  SUPER = 'super',     // purple tint
  ULTRA = 'ultra',     // green tint
  GOLD = 'gold',       // gold tint + sparkle
}

export interface GradeModifiers {
  hpMult: number;
  speedMult: number;
  damageMult: number;
  tint: number;          // sprite tint color
  rewardMult: number;    // bonus currency
  label: string;         // suffix shown in name
  labelColor: string;    // text color for grade badge
}

export const GRADE_MODIFIERS: Record<EnemyGrade, GradeModifiers> = {
  [EnemyGrade.NORMAL]: { hpMult: 1,   speedMult: 1,   damageMult: 1,   tint: 0, rewardMult: 1,   label: '',         labelColor: '#aaaaaa' },
  [EnemyGrade.ELITE]:  { hpMult: 2,   speedMult: 0.9, damageMult: 1.5, tint: 0xff4444, rewardMult: 2,   label: ' [Elite]', labelColor: '#ff6666' },
  [EnemyGrade.BOSS]:   { hpMult: 1.5, speedMult: 0.8, damageMult: 3,   tint: 0x4488ff, rewardMult: 3,   label: ' [Boss]',  labelColor: '#6699ff' },
  [EnemyGrade.SUPER]:  { hpMult: 3,   speedMult: 0.7, damageMult: 2,   tint: 0xaa44ff, rewardMult: 4,   label: ' [Super]', labelColor: '#bb66ff' },
  [EnemyGrade.ULTRA]:  { hpMult: 0.8, speedMult: 2,   damageMult: 2,   tint: 0x44ff66, rewardMult: 3.5, label: ' [Ultra]', labelColor: '#66ff88' },
  [EnemyGrade.GOLD]:   { hpMult: 4,   speedMult: 0.6, damageMult: 2.5, tint: 0xffcc44, rewardMult: 6,   label: ' [Gold]',  labelColor: '#ffdd66' },
};

// Reference mass for speed calculation (typical light robot)
export const SPEED_REFERENCE_MASS = 25;

// Spacing between component centers in a robot assembly (px).
// Components are flush side-by-side at this value (matches typical component width).
export const COMPONENT_SPACING = 20;

// Robot size multiplier relative to tile size.
// Controls how many pixels one component cell occupies on the map.
// Formula: cellScreenPx = ROBOT_TILE_SCALE * TILE_SIZE
// At 0.25: one cell = 10px → a 3-wide robot = 30px (< 1 tile)
// At 0.50: one cell = 20px → a 3-wide robot = 60px (1.5 tiles)
export const ROBOT_TILE_SCALE = 0.3;

// ── Enemy weapon types ──
export enum EnemyWeaponType {
  CANNON_NORMAL = 'cn',     // Standard projectile
  CANNON_HEAVY  = 'cl',     // Slow heavy projectile, bigger
  BURST         = 'r',      // 3-shot rapid burst
  FLAMETHROWER  = 'lf',     // Short-range cone of flame
  LASER         = 'lz',     // Instant-hit beam
  MISSILE_AIR   = 'ma',     // Arcing homing missile
  ACID_CATAPULT = 'ac',     // Lobbed acid blob — AoE splash + poison DoT
}

export interface EnemyWeaponDef {
  type: EnemyWeaponType;
  damage: number;
  range: number;           // pixels
  cooldown: number;        // seconds between shots
  speed: number;           // projectile speed px/s (0 for beam/flame)
  color: number;           // projectile / beam color
  burstCount?: number;     // for BURST: number of shots per burst
  burstDelay?: number;     // for BURST: ms between each shot
  coneAngle?: number;      // for FLAMETHROWER: half-angle in radians
  flameDuration?: number;  // for FLAMETHROWER: how long the flame lasts (seconds)
  splashRadius?: number;   // for ACID_CATAPULT: AoE damage radius (px)
  poisonDuration?: number; // for ACID_CATAPULT: poison DoT duration (seconds)
  poisonDps?: number;      // for ACID_CATAPULT: poison damage per second (stackable)
}

// Enemy base stats per type
// speed = tiles per second (multiplied by TILE_SIZE at runtime for px/s)
// mass affects speed scaling with wave progression
// cloakDuration > 0 means the enemy can cloak for that many seconds (cooldown = cloakCooldown)
export interface EnemyStat {
  hp: number; speed: number; mass: number; radius: number;
  color: number; reward: number; label: string; baseDamage: number;
  meleeCooldown: number;    // seconds between melee attacks
  cloakDuration: number; cloakCooldown: number;
  // Multi-weapon loadout (replaces old single-shoot system)
  weapons: EnemyWeaponDef[];
}

export const ENEMY_STATS: Record<EnemyType, EnemyStat> = {
  // ── Ground ──
  // GRUNT: CQC + Canon Normal
  [EnemyType.GRUNT]: {
    hp: 100, speed: 1, mass: 50, radius: 10,
    color: 0xff4444, reward: 1, label: 'Grunt', baseDamage: 5,
    meleeCooldown: 1.0,
    cloakDuration: 0, cloakCooldown: 0,
    weapons: [
      { type: EnemyWeaponType.CANNON_NORMAL, damage: 8, range: 260, cooldown: 1.5, speed: 180, color: 0xff4444 },
    ],
  },
  // RUNNER: CQC only
  [EnemyType.RUNNER]: {
    hp: 50, speed: 2, mass: 25, radius: 7,
    color: 0xffaa22, reward: 1, label: 'Runner', baseDamage: 4,
    meleeCooldown: 0.5,
    cloakDuration: 0, cloakCooldown: 0,
    weapons: [],
  },
  // TANK: CQC + Canon Lourd
  [EnemyType.TANK]: {
    hp: 300, speed: 0.6, mass: 120, radius: 14,
    color: 0x8844cc, reward: 4, label: 'Tank', baseDamage: 12,
    meleeCooldown: 1.5,
    cloakDuration: 0, cloakCooldown: 0,
    weapons: [
      { type: EnemyWeaponType.CANNON_HEAVY, damage: 30, range: 160, cooldown: 2.5, speed: 140, color: 0x882222 },
    ],
  },
  // SWARM: CQC only
  [EnemyType.SWARM]: {
    hp: 30, speed: 1.5, mass: 15, radius: 5,
    color: 0x44ff88, reward: 1, label: 'Swarm', baseDamage: 3,
    meleeCooldown: 0.4,
    cloakDuration: 0, cloakCooldown: 0,
    weapons: [],
  },
  // BRUTE: CQC + Canon Lourd
  [EnemyType.BRUTE]: {
    hp: 500, speed: 0.6, mass: 200, radius: 16,
    color: 0x664488, reward: 6, label: 'Brute', baseDamage: 18,
    meleeCooldown: 2.0,
    cloakDuration: 0, cloakCooldown: 0,
    weapons: [
      { type: EnemyWeaponType.CANNON_HEAVY, damage: 40, range: 170, cooldown: 2.5, speed: 120, color: 0x553388 },
    ],
  },
  // PHANTOM: CQC + Laser
  [EnemyType.PHANTOM]: {
    hp: 80, speed: 1.3, mass: 35, radius: 9,
    color: 0x225588, reward: 2, label: 'Phantom', baseDamage: 7,
    meleeCooldown: 0.8,
    cloakDuration: 3, cloakCooldown: 8,
    weapons: [
      { type: EnemyWeaponType.LASER, damage: 15, range: 200, cooldown: 1.5, speed: 0, color: 0x44aaff },
    ],
  },
  // ── Flying ──
  // DRONE: Canon Normal + Missiles Air
  [EnemyType.DRONE]: {
    hp: 100, speed: 1.2, mass: 20, radius: 8,
    color: 0xcc88ff, reward: 2, label: 'Drone', baseDamage: 5,
    meleeCooldown: 1.5,
    cloakDuration: 0, cloakCooldown: 0,
    weapons: [
      { type: EnemyWeaponType.CANNON_NORMAL, damage: 12, range: 130, cooldown: 1.3, speed: 220, color: 0x9944ff },
      { type: EnemyWeaponType.MISSILE_AIR, damage: 25, range: 180, cooldown: 3.0, speed: 160, color: 0xff8844 },
    ],
  },
  // BOMBER: CQC + Lance-Flamme
  [EnemyType.BOMBER]: {
    hp: 200, speed: 0.7, mass: 60, radius: 12,
    color: 0xbb4488, reward: 5, label: 'Bomber', baseDamage: 14,
    meleeCooldown: 1.5,
    cloakDuration: 0, cloakCooldown: 0,
    weapons: [
      { type: EnemyWeaponType.FLAMETHROWER, damage: 10, range: 80, cooldown: 1.0, speed: 0, color: 0xff6622,
        coneAngle: Math.PI / 6, flameDuration: 0.8 },
    ],
  },
  // WASP: Rafales + Missiles Air
  [EnemyType.WASP]: {
    hp: 45, speed: 1.5, mass: 10, radius: 5,
    color: 0xffcc44, reward: 1, label: 'Wasp', baseDamage: 3,
    meleeCooldown: 0.6,
    cloakDuration: 0, cloakCooldown: 0,
    weapons: [
      { type: EnemyWeaponType.BURST, damage: 5, range: 110, cooldown: 1.5, speed: 250, color: 0xffcc44,
        burstCount: 3, burstDelay: 100 },
      { type: EnemyWeaponType.MISSILE_AIR, damage: 20, range: 160, cooldown: 3.5, speed: 150, color: 0xff8844 },
    ],
  },
  // SPECTER: Laser + Missiles Air
  [EnemyType.SPECTER]: {
    hp: 90, speed: 1.5, mass: 25, radius: 8,
    color: 0x3366aa, reward: 3, label: 'Specter', baseDamage: 7,
    meleeCooldown: 1.2,
    cloakDuration: 3, cloakCooldown: 7,
    weapons: [
      { type: EnemyWeaponType.LASER, damage: 20, range: 200, cooldown: 1.8, speed: 0, color: 0x2255cc },
      { type: EnemyWeaponType.MISSILE_AIR, damage: 25, range: 180, cooldown: 3.0, speed: 180, color: 0x4488ff },
    ],
  },
  // HIVE: Ground crawler, acid catapult, spawns Wasps on death
  [EnemyType.HIVE]: {
    hp: 400, speed: 0.4, mass: 80, radius: 14,
    color: 0x88aa22, reward: 5, label: 'Hive', baseDamage: 10,
    meleeCooldown: 1.5,
    cloakDuration: 0, cloakCooldown: 0,
    weapons: [
      { type: EnemyWeaponType.ACID_CATAPULT, damage: 18, range: 200, cooldown: 3.5, speed: 140, color: 0x66ff22,
        splashRadius: 45, poisonDuration: 5, poisonDps: 4 },
    ],
  },
  // HEALER: Support unit, periodically heals nearby enemies. No weapons — high priority target.
  [EnemyType.HEALER]: {
    hp: 150, speed: 0.7, mass: 40, radius: 10,
    color: 0x44ff88, reward: 4, label: 'Healer', baseDamage: 5,
    meleeCooldown: 1.5,
    cloakDuration: 0, cloakCooldown: 0,
    weapons: [],
  },
};

// Enemy type → texture key mapping
export const ENEMY_TEXTURES: Record<EnemyType, string> = {
  [EnemyType.GRUNT]:   'enemy_grunt',
  [EnemyType.RUNNER]:  'enemy_runner',
  [EnemyType.TANK]:    'enemy_tank',
  [EnemyType.SWARM]:   'enemy_swarm',
  [EnemyType.BRUTE]:   'enemy_brute',
  [EnemyType.PHANTOM]: 'enemy_phantom',
  [EnemyType.DRONE]:   'enemy_drone',
  [EnemyType.BOMBER]:  'enemy_bomber',
  [EnemyType.WASP]:    'enemy_wasp',
  [EnemyType.SPECTER]: 'enemy_specter',
  [EnemyType.HIVE]:    'enemy_hive',
  [EnemyType.HEALER]:  'enemy_healer',
};

// Enemy spritesheet file mapping (public/pixelart/ennemies/)
// All spritesheets are 1152x128 = 9 frames of 128x128
export const ENEMY_SPRITE_FILES: Record<EnemyType, { file: string; attack?: string; death?: string }> = {
  [EnemyType.GRUNT]:   { file: 'grunt',   attack: 'grunt',   death: 'grunt' },
  [EnemyType.RUNNER]:  { file: 'runner',  attack: 'runner',  death: 'runner' },
  [EnemyType.TANK]:    { file: 'tank' }, // no attack/death sheets
  [EnemyType.SWARM]:   { file: 'swarm',   attack: 'swarm',   death: 'swarm' },
  [EnemyType.BRUTE]:   { file: 'Brute',   attack: 'Brute',   death: 'Brute' },
  [EnemyType.PHANTOM]: { file: 'Phantom', attack: 'Phantom', death: 'Phantom' },
  [EnemyType.DRONE]:   { file: 'Drone',   attack: 'Drone',   death: 'Drone' },
  [EnemyType.BOMBER]:  { file: 'Bomber',  attack: 'Bomber',  death: 'Bomber' },
  [EnemyType.WASP]:    { file: 'Wasp',    attack: 'Wasp',    death: 'Wasp' },
  [EnemyType.SPECTER]: { file: 'Specter', attack: 'Specter', death: 'Specter' },
  [EnemyType.HIVE]:    { file: 'Hive',    attack: 'Hive',    death: 'Hive' },
  [EnemyType.HEALER]:  { file: 'Healer',  attack: 'Healer',  death: 'Healer' },
};

// Flying enemy types set
export const FLYING_ENEMY_TYPES: ReadonlySet<EnemyType> = new Set([
  EnemyType.DRONE, EnemyType.BOMBER, EnemyType.WASP, EnemyType.SPECTER, EnemyType.SWARM,
]);

// Mass threshold above which enemies are immune to knockback
export const KNOCKBACK_IMMUNE_MASS = 100;

// Enemy types that aggro toward nearby player robots instead of going straight to base
export const AGGRO_ENEMY_TYPES: ReadonlySet<EnemyType> = new Set([
  EnemyType.GRUNT, EnemyType.SWARM, EnemyType.WASP, EnemyType.HIVE,
]);

// Aggro detection range in pixels (must be >= longest enemy weapon range to allow ranged engagement)
export const AGGRO_RANGE = 280;

// Contact damage: DPS dealt by melee enemies touching a player robot
export const CONTACT_DPS = 5;

// Difficulty: multi-parameter scaling (adjusted via options menu)
// Each cran above Moderate: -2% rewards, +2% enemy speed, +5 base power
// Each cran below Moderate: inverse bonuses
export const DIFFICULTY_LEVELS = [
  { name: 'Normal',   mult: 1.00, rewardMult: 1.00, speedMult: 1.00, powerOffset: 0   },
  { name: 'Hard',     mult: 1.50, rewardMult: 0.96, speedMult: 1.04, powerOffset: 10  },
  { name: 'Infernal', mult: 3.00, rewardMult: 0.88, speedMult: 1.12, powerOffset: 30  },
] as const;

export let ENEMY_DAMAGE_MULTIPLIER = 1.0;
export let DIFFICULTY_REWARD_MULT = 1.0;
export let DIFFICULTY_SPEED_MULT = 1.0;
export let DIFFICULTY_POWER_OFFSET = 0;

export function setEnemyDamageMultiplier(value: number): void {
  ENEMY_DAMAGE_MULTIPLIER = value;
}

/** Apply all difficulty parameters from a single level entry */
export function setDifficultyParams(level: typeof DIFFICULTY_LEVELS[number]): void {
  ENEMY_DAMAGE_MULTIPLIER = level.mult;
  DIFFICULTY_REWARD_MULT = level.rewardMult;
  DIFFICULTY_SPEED_MULT = level.speedMult;
  DIFFICULTY_POWER_OFFSET = level.powerOffset;
}

// Base
export const BASE_HP = 100;
export const BASE_UPGRADE_HP = 100;           // HP added per upgrade level
export const BASE_UPGRADE_BASE_COST = 100;    // first upgrade cost ($)
// Enemy base HP scales exponentially: round(100 * 1.08^tier)
// tier0=100, tier13≈270, tier25≈730, tier40≈2000, tier65≈14800, tier99≈110000
export const ENEMY_BASE_HP_BASE = 100;
export const ENEMY_BASE_HP_GROWTH = 1.08;

// ── Multi-axis base upgrade system ──
export enum BaseUpgradeAxis {
  HP = 'hp',
  ARMOR = 'armor',
  RANGE = 'range',
  CANNONS = 'cannons',
  FIREPOWER = 'firepower',
}

export interface BaseUpgradeAxisDef {
  axis: BaseUpgradeAxis;
  label: string;               // display key for i18n
  maxLevel: number;
  baseCost: number;            // resources cost at level 1
  costGrowth: number;          // exponential cost multiplier per level
  currencyCost?: number;       // optional campaign currency cost at level 1 (same growth)
  effectPerLevel: number;      // the raw value added per level
  effectUnit: string;          // display unit (HP, %, tiles, etc.)
  color: number;               // bar fill color
}

export const BASE_UPGRADE_DEFS: BaseUpgradeAxisDef[] = [
  {
    axis: BaseUpgradeAxis.HP, label: 'base.axis.hp',
    maxLevel: 10, baseCost: 80, costGrowth: 1.8,
    effectPerLevel: 100, effectUnit: 'HP', color: 0x44ff44,
  },
  {
    axis: BaseUpgradeAxis.ARMOR, label: 'base.axis.armor',
    maxLevel: 10, baseCost: 100, costGrowth: 1.8,
    effectPerLevel: 5, effectUnit: '%', color: 0x8888ff,
  },
  {
    axis: BaseUpgradeAxis.RANGE, label: 'base.axis.range',
    maxLevel: 8, baseCost: 120, costGrowth: 2.0,
    effectPerLevel: 0.5, effectUnit: 'tiles', color: 0xffcc44,
  },
  {
    axis: BaseUpgradeAxis.CANNONS, label: 'base.axis.cannons',
    maxLevel: 5, baseCost: 250, costGrowth: 2.5,
    currencyCost: 50,  // also costs campaign currency
    effectPerLevel: 1, effectUnit: '', color: 0xff6644,
  },
  {
    axis: BaseUpgradeAxis.FIREPOWER, label: 'base.axis.firepower',
    maxLevel: 10, baseCost: 80, costGrowth: 1.8,
    effectPerLevel: 2, effectUnit: 'dmg', color: 0xff44aa,
  },
];

// Fog of War
export const BASE_VISION_RADIUS = 3;    // tiles — permanent reveal + radar around base
export const ROBOT_EXPLORE_RADIUS = 3;  // tiles — permanent reveal around robots (larger than radar/sensor range)
export const FOG_PENUMBRA_RADIUS = 2;   // tiles — semi-transparent zone beyond explored (placement allowed)

// Total waves per map
export const TOTAL_WAVES = 5;

// Wave config
export const WAVE_CONFIG = {
  spawnDelay: 800,    // ms between each enemy spawn
  waveDelay: 3000,    // ms after wave complete before next can start
} as const;

// Wave composition entry
export interface WaveEntry {
  type: EnemyType;
  count: number;
}

// Base enemy count per wave (scales with wave number within the map)
// Wave 1 = light intro, Wave 5 = final assault
export const WAVE_BASE_COUNTS: Record<EnemyType, number[]> = {
  // [wave1, wave2, wave3, wave4, wave5] — base counts before map scaling
  [EnemyType.GRUNT]:   [5, 4, 4, 3, 3],
  [EnemyType.RUNNER]:  [0, 3, 3, 4, 4],
  [EnemyType.SWARM]:   [0, 0, 6, 8, 12],
  [EnemyType.TANK]:    [0, 0, 1, 2, 3],
  [EnemyType.PHANTOM]: [0, 0, 1, 2, 3],
  [EnemyType.DRONE]:   [0, 2, 2, 3, 4],
  [EnemyType.WASP]:    [0, 0, 4, 5, 8],
  [EnemyType.BRUTE]:   [0, 0, 0, 1, 2],
  [EnemyType.BOMBER]:  [0, 0, 1, 2, 3],
  [EnemyType.SPECTER]: [0, 0, 1, 2, 3],
  [EnemyType.HIVE]:    [0, 0, 1, 1, 2],
  [EnemyType.HEALER]:  [0, 0, 1, 1, 2],
};

// ── Enemy damage resistance / weakness matrix ──
// Multiplier applied to incoming damage based on enemy type vs weapon projectile type.
// 🎯 1.5 = weak point   ✅ 1.0 = normal   ⚠️ 0.6 = resistant   ❌ 0.3 = very resistant
import { ProjectileType } from '@/components/ComponentDefs';

export const ENEMY_RESISTANCES: Record<EnemyType, Partial<Record<ProjectileType, number>>> = {
  //                          BULLET  SHELL   MISSILE  FLAME   BEAM
  [EnemyType.GRUNT]:   { [ProjectileType.BULLET]: 1.0, [ProjectileType.SHELL]: 1.5, [ProjectileType.MISSILE_ARC]: 1.0, [ProjectileType.FLAME]: 1.5, [ProjectileType.BEAM]: 1.0 },
  [EnemyType.RUNNER]:  { [ProjectileType.BULLET]: 1.0, [ProjectileType.SHELL]: 0.6, [ProjectileType.MISSILE_ARC]: 0.6, [ProjectileType.FLAME]: 0.3, [ProjectileType.BEAM]: 1.5 },
  [EnemyType.TANK]:    { [ProjectileType.BULLET]: 0.5, [ProjectileType.SHELL]: 1.0, [ProjectileType.MISSILE_ARC]: 1.5, [ProjectileType.FLAME]: 0.3, [ProjectileType.BEAM]: 0.6 },
  [EnemyType.SWARM]:   { [ProjectileType.BULLET]: 1.0, [ProjectileType.SHELL]: 0.6, [ProjectileType.MISSILE_ARC]: 1.5, [ProjectileType.FLAME]: 1.5, [ProjectileType.BEAM]: 0.3 },
  [EnemyType.BRUTE]:   { [ProjectileType.BULLET]: 0.5, [ProjectileType.SHELL]: 1.0, [ProjectileType.MISSILE_ARC]: 1.5, [ProjectileType.FLAME]: 0.3, [ProjectileType.BEAM]: 0.6 },
  [EnemyType.PHANTOM]: { [ProjectileType.BULLET]: 0.6, [ProjectileType.SHELL]: 0.6, [ProjectileType.MISSILE_ARC]: 0.6, [ProjectileType.FLAME]: 1.0, [ProjectileType.BEAM]: 1.5 },
  [EnemyType.DRONE]:   { [ProjectileType.BULLET]: 1.5, [ProjectileType.SHELL]: 0.6, [ProjectileType.MISSILE_ARC]: 1.5, [ProjectileType.FLAME]: 0.6, [ProjectileType.BEAM]: 1.0 },
  [EnemyType.BOMBER]:  { [ProjectileType.BULLET]: 0.6, [ProjectileType.SHELL]: 1.0, [ProjectileType.MISSILE_ARC]: 1.5, [ProjectileType.FLAME]: 0.6, [ProjectileType.BEAM]: 1.0 },
  [EnemyType.WASP]:    { [ProjectileType.BULLET]: 0.6, [ProjectileType.SHELL]: 0.3, [ProjectileType.MISSILE_ARC]: 0.6, [ProjectileType.FLAME]: 1.0, [ProjectileType.BEAM]: 1.5 },
  [EnemyType.SPECTER]: { [ProjectileType.BULLET]: 0.6, [ProjectileType.SHELL]: 0.6, [ProjectileType.MISSILE_ARC]: 1.0, [ProjectileType.FLAME]: 0.6, [ProjectileType.BEAM]: 1.5 },
  [EnemyType.HIVE]:    { [ProjectileType.BULLET]: 0.6, [ProjectileType.SHELL]: 1.0, [ProjectileType.MISSILE_ARC]: 1.5, [ProjectileType.FLAME]: 1.5, [ProjectileType.BEAM]: 0.6 },
  // Healer: fragile to flames and explosives, resistant to beams (energy shield aura)
  [EnemyType.HEALER]:  { [ProjectileType.BULLET]: 1.0, [ProjectileType.SHELL]: 1.5, [ProjectileType.MISSILE_ARC]: 1.5, [ProjectileType.FLAME]: 1.5, [ProjectileType.BEAM]: 0.6 },
};

/** Look up the resistance multiplier for an enemy vs a projectile type. Returns 1.0 if not defined. */
export function getResistanceMultiplier(enemyType: EnemyType, projType: ProjectileType): number {
  return ENEMY_RESISTANCES[enemyType]?.[projType] ?? 1.0;
}

// Mud tile speed penalty (multiplier applied to ground unit speed on MUD tiles)
export const MUD_SPEED_MULTIPLIER = 0.4;

// ── Terrain modifiers for robots ──
// Each TileType can grant buffs/debuffs to robots standing on it.
// speedMult/rangeMult/fireRateMult: 1.0 = neutral, >1 = buff, <1 = debuff
// label: short text shown as popup when robot enters the tile
// color: tint for the status indicator
export interface TerrainModifier {
  speedMult: number;
  rangeMult: number;
  fireRateMult: number;
  label: string;
  color: number;
}

export const TERRAIN_MODIFIERS: Partial<Record<TileType, TerrainModifier>> = {
  [TileType.PLACEMENT]: { speedMult: 1.0, rangeMult: 1.10, fireRateMult: 1.0, label: 'ELEVATED +10% range', color: 0x44aaff },
  [TileType.BROKEN_PLACEMENT]: { speedMult: 0.9, rangeMult: 1.0, fireRateMult: 0.9, label: 'DAMAGED -10% spd/FR', color: 0xaa6644 },
  [TileType.GRILLE]:    { speedMult: 1.0, rangeMult: 1.0,  fireRateMult: 1.15, label: 'CONDUIT +15% fire rate', color: 0xffcc44 },
  [TileType.PATH]:      { speedMult: 1.15, rangeMult: 1.0, fireRateMult: 1.0,  label: 'ROAD +15% speed', color: 0x88ff44 },
  [TileType.MUD]:       { speedMult: 0.7,  rangeMult: 1.0, fireRateMult: 0.9,  label: 'MUD -30% spd -10% FR', color: 0x886633 },
};

// Wave bonus: resources awarded on wave clear
export const WAVE_CLEAR_BONUS = 20;
